import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { HubConnectionState } from '@aspnet/signalr';

import userImg from 'img/header-user-example.svg';
import important from 'img/icon-important.svg';
import logoGroup from 'img/header-mafra-group.svg';
import logoCCare from 'img/customer-care-header-logo.svg';
import IconArrowDown from 'img/arrow-down.svg';

import SwitchComponent from 'components/SwitchComponent';
import { useCustomEvent } from 'components/ApplicationInsights';
import {
  protocolosAction,
  pedidosAction,
  abrirProtocoloAction,
  EnumShippingCompany
} from 'components/ApplicationInsights/eventNames';
import Text from '../Text';
import Dropdown from './Dropdown';
import { Button } from '../Button';
import Animated from '../Animated';
import { Alert } from 'components/Alert';
import SearchHeader from './SearchHeader';
import { Row } from 'components/Layout/Grid';
import NotifyedContainer from '../NotifyedContainer';
import Sidebar from 'pages/Quotes/components/Sidebar';
import { DropdownCustomers } from './DropdownCustomers';
import { DropdownCustomerForSupplier } from './DropdownCustomerForSupplier';
import { DownloadBar } from '../DownloadBar';
import { Visibility } from 'components/Visibility';
import { Banner } from 'components/Banner';

import { IUser } from 'models/User';
import { ICustomer } from 'models/Customer';
import { OrdersTabsEnum } from 'models/Order';

import KeycloakPubServiceInstance from 'services/keycloakpubsub.service';
import DomainSettingService from 'services/domainSetting.service';

import { SelectCustomerIdIsArray } from 'utils/SelectCustomerIdIsArray';
import keycloak from 'utils/keycloak';
import { isMobileDevice } from 'utils/ScreenUtils';

import { useLoadCte } from 'hooks/useLoadCte';
import { useHubNotification } from 'hooks/useHubNotification';

import { HeaderMenuItems } from './interface';

import { Container } from './styles';

export const Header: React.FC = () => {

  const dispatch = useDispatch();

  const { hasCte } = useLoadCte();

  const user = useSelector((state: { user: IUser; }) => state.user);
  const customer = useSelector((state: { customer: ICustomer; }) => state.customer);

  const navigate = useNavigate();
  const location = useLocation();

  const [eventClick, setEventClick] = useState('');
  const [dropdownExpanded, toggleDropdownExpanded] = useState(false);
  const [headerMenuItems, setHeaderMenuItems] = useState<HeaderMenuItems[]>([
    { name: 'Protocolos', linkTo: '/protocolos', notifications: 0, type: 1, trackItem: protocolosAction.HEADER_PROTOCOLOS },
    { name: 'Pedidos', linkTo: '/pedidos', notifications: 0, type: 2, trackItem: pedidosAction.HEADER_PEDIDOS },
  ]);

  let preloadedMenuHeader: HeaderMenuItems[] = [
    { name: 'Protocolos', linkTo: '/protocolos', notifications: 0, type: 1, trackItem: protocolosAction.HEADER_PROTOCOLOS },
    { name: 'Pedidos', linkTo: '/pedidos', notifications: 0, type: 2, trackItem: pedidosAction.HEADER_PEDIDOS },
  ];

  const componentName = `Menu`;
  const trackMenuClick = useCustomEvent(componentName, eventClick);

  const _domainSettingService = new DomainSettingService();

  const isMobile = isMobileDevice();

  const hubConnection = useHubNotification();

  useEffect(() => {
    resetCurrentTabPage();
  }, [location]);

  useEffect(() => {
    initNotificationSocket();
  }, []);

  useEffect(() => {
    if (keycloak.token)
      getEnterprisesTalkPresident();
  }, [keycloak.token]);


  useEffect(() => {

    addQuoteMenu();

    addShippingCompanyMenu();

    if (user?.userType === 'supplier') {
      setHeaderMenuItems(headerMenuItems.filter((item) => item.name === 'Pedidos'));
    }

  }, [customer, user]);

  const addQuoteMenu = () => {
    const typeUser = user.userType && user.userType === 'customer';
    const index = preloadedMenuHeader.findIndex(x => x.name === 'Cotações');

    if (user.userType && index === -1) {
      const linkMenu = typeUser ? '/cotacoes' : '/cotacoes-dashboard';
      preloadedMenuHeader.splice(1, 0, {
        name: 'Cotações', linkTo: linkMenu, notifications: 0, type: Number(`${typeUser ? 3 : 5}`), trackItem: pedidosAction.HEADER_COTACOES
      });
    }
  };

  const addShippingCompanyMenu = () => {

    const index = preloadedMenuHeader.findIndex(x => x.name === 'Healthlog');

    if (index > 0 && hasCte) return false;

    if (hasCte && index === -1) {
      preloadedMenuHeader.push({
        name: 'Healthlog',
        linkTo: '/cte',
        notifications: 0,
        type: 5,
        trackItem: EnumShippingCompany.HEADER_TRANSPORTADORA
      });
    } else {
      preloadedMenuHeader = preloadedMenuHeader.filter(x => x.name !== 'Healthlog');
    }

    setHeaderMenuItems(preloadedMenuHeader);
  };

  const initNotificationSocket = () => {
    KeycloakPubServiceInstance.subscribe(async () => {
      if (keycloak.authenticated && hubConnection.state === HubConnectionState.Disconnected) {
        hubConnection.start().then(() => {
          [1, 2, 3].forEach((item) => {
            updateCounter(item);
          });
        });
      }
    });
  };

  const updateCounter = (item: number) => {
    hubConnection.invoke('getInitialCount', item).then((count) => {
      setHeaderValues(item, count);
    });
  };

  const setHeaderValues = (type: number, count: number) => {
    setHeaderMenuItems((prevstate) => {
      const menuItems = prevstate.map((item, _) => {
        if (item.type === type) {
          return { ...item, notifications: count };
        }
        return { ...item };
      });

      return menuItems;
    });
  };

  const getEnterprisesTalkPresident = async () => {
    const response = await _domainSettingService
      .get(undefined, undefined, undefined, {
        and: [
          { identifier: { eq: 'EnterprisesTalkPresident' } },
          { enabled: { eq: true } },
        ],
      });

    if (response && response.data) {
      const enterpriseVips = response.data[0].value.split(',');
      dispatch({
        type: 'SET_ENTERPRISE_VIPS',
        data: enterpriseVips
      });
    }
  };

  hubConnection.on('receiveNotification', (type) => {
    updateCounter(type);
  });

  hubConnection.on('receiveMessageAsync', (data) => {

    const message = JSON.parse(data);
    dispatch({
      type: "OPEN_DOWNLOAD_BAR",
      data: {
        url: message.FileUrl,
        error: message.ErrorMessage,
      }
    });
  });

  const setupReconnect = () => {
    if (hubConnection.state === HubConnectionState.Disconnected) {
      hubConnection.start()
        .then(() => console.info('Conexão com hub reestabelecida'))
        .catch(() => {});
    }
  };

  hubConnection.onclose(() => {
    setTimeout(setupReconnect, 2000);
  });

  const checkConnection = async (param: () => Promise<void>) => {
    if (hubConnection.state === HubConnectionState.Connected) {
      param().then(() => { });
    } else {
      hubConnection.start().then(() => {
        param().then(() => { });
      }).catch(() => console.warn('Error on checkConnection'));
    }
  };

  const readNotification = (type: number) => {
    checkConnection(async () => {
      hubConnection.invoke('read', type);
    });
  };

  async function setOpenProtocolPath() {
    if (SelectCustomerIdIsArray())
      return;
    if (location.pathname === '/abrir-protocolo') {
      return window.location.reload();
    }

    setEventClick('Abrir Protocolo');
    trackMenuClick({ item: abrirProtocoloAction.HEADER_ABRIRPROTOCOLO });

    return navigate('/abrir-protocolo');
  }

  function setHomeName() {
    return user.customer?.companyName || 'VIVEO';
  }

  async function clickMenu(item: any) {
    setEventClick(item.name);
    trackMenuClick({ item: item.trackItem });
    readNotification(item.type);
    navigate(item.linkTo);
  }

  function goToCustomerData() {
    if (SelectCustomerIdIsArray()) return;
    navigate('dados-cliente');
  }

  const legalDocuments = useMemo(() => {
    let qtd = 0;
    if (user && user.userType === 'customer') {
      user.customersRoles?.map((customerRole) => {
        if (customerRole.quantityLegalDocument)
          qtd += customerRole.quantityLegalDocument;
      });
    }

    return qtd;
  }, [user, location]);

  const legalDocumentClassificationType = useMemo(() => {
    let qtd = 0;
    if (user && user.userType === 'customer') {
      user.customersRoles?.map((customerRole) => {
        if (customerRole.legalDocumentClassificationType && customerRole.legalDocumentClassificationType === 1)
          qtd += 1;
      });
    }

    return qtd;
  }, [user, location]);

  function setMessageAlertLegalDocument() {
    if (legalDocuments) {
      if (legalDocuments > 1) {
        return 'documentos legais estão próximos';
      } else {
        return 'documento legal esta próximo';
      }
    }
  }

  function setMessageDisclaimerDocument() {
    if (legalDocuments > 0) {
      return `identificamos que ${legalDocuments} ${setMessageAlertLegalDocument()} da data de vigência. Clique aqui para providenciar a atualização`;
    } else if (legalDocuments === 0 && legalDocumentClassificationType > 0) {
      return `Documentos provisórios recebidos. Favor enviar documentação final.`;
    }
  }

  const setAlertLegalDocument = useCallback(() => {
    if ((location.pathname === '/dashboard' || location.pathname === '/') &&
      ((legalDocuments && legalDocuments > 0) ||
        (legalDocumentClassificationType && legalDocumentClassificationType > 0))) {
      return (
        <SwitchComponent roles={['administrator', 'member']} type={['customer']}>
          <Alert
            title="Importante:"
            description={setMessageDisclaimerDocument()}
            img={important}
            className="background-alert-header"
            titleFontSize={14}
            descriptionFontSize={14}
            onClick={goToCustomerData}
          />
        </SwitchComponent>
      );
    }
  }, [user, legalDocuments, location]);

  const resetCurrentTabPage = () => {
    dispatch({
      type: 'SET_CURRENT_TAB_PAGE',
      data: 0
    });

    dispatch({
      type: 'SET_CURRENT_TAB',
      data: OrdersTabsEnum.PEDIDO_FATURADO
    });
  };

  const dashLink = () => {
    if (isMobile) return;
    toggleDropdownExpanded(false);
    navigate("/dashboard");
  };

  return (
    <Container>
      {setAlertLegalDocument()}
      <DownloadBar />
      <header className={`px-sm-2 p-3 relative z-6 ${location.pathname === '/carta-correcao' || location.pathname === '/manutencao' ? 'carta-correcao' : ''}`}>
        <a href='#' onClick={() => dashLink()}>
          <div className="header-logo-box mr-2">
            <img
              className="viveo"
              src={logoGroup}
              alt="logo viveo"
            />
            <img
              className="ccare"
              src={logoCCare}
              alt="logo ccare"
            />
          </div>
        </a>

        <div className="header-input-search-box d-flex flex-1 ml-1 mr-3 d-md-none">
          <SearchHeader closeDropdown={() => toggleDropdownExpanded(false)} />
          <div className="header-menu-items-box">
            {headerMenuItems.map((item, _) => (
              <a
                onClick={() => clickMenu(item)}
                className={_ > 0 ? 'ml-2' : ''}
                key={_}
                href='#'
              >
                <NotifyedContainer
                  notification={item.notifications}
                  className={`
                    ${user?.userType === 'customer' ? 'hide-menu' : 'd-lg-none'}
                  `}
                >
                  <Text className="color-blue-gray" size={14}>
                    {item.name}
                  </Text>
                </NotifyedContainer>
              </a>
            ))}
          </div>
          <Visibility visible={user?.userType !== 'supplier'}>
            <Button
              onClick={setOpenProtocolPath}
              className={`outline ${user?.userType === 'customer' ? 'hide-button' : 'd-lg-none'}`}
              width="auto"
              id="open-protocol"
            >
              <Text size={14} className="color-primary font-bold px-1">
                Abrir Protocolo
              </Text>
            </Button>
          </Visibility>
        </div>

        <SwitchComponent roles={['administrator', 'member']} type={['customer']}>
          <DropdownCustomers {...user} />
        </SwitchComponent>

        <SwitchComponent roles={['administrator', 'supplier']} type={['supplier']}>
          <DropdownCustomerForSupplier />
        </SwitchComponent>

        <div className="header-user-info-box relative">
          <button
            className="d-flex align-center"
            onClick={() => toggleDropdownExpanded(!dropdownExpanded)}
          >
            <div className="header-user-img-box">
              <img src={user.fileUrl || userImg} alt="" />
            </div>
            <Row className="overflow-x-hidden w-sm-auto" style={{ width: 150 }}>
              <div className="d-flex flex-column ml-2 pr-5 d-sm-none overflow-x-hidden">
                <Text size={14} className="color-blue-gray">
                  Olá {user?.fullName || 'Usuário'},
                </Text>
                <Text size={10} className="color-blue-gray text-left">
                  {setHomeName()}
                </Text>
              </div>
            </Row>
            <img
              className="d-sm-none"
              src={IconArrowDown}
              alt=""
              style={{
                transform: `${dropdownExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }`,
                transition: 'all 0.5s ease',
              }}
            />
          </button>
        </div>
      </header>
      <Animated
        show={dropdownExpanded}
        animationIn="slideInDown"
        animationInDelay="faster"
        animationOut="slideOutUp"
        animationOutDelay="faster"
        className="absolute left-sm-0 right-0 z-5"
      >
        <Dropdown closeDropdown={() => toggleDropdownExpanded(false)} />
      </Animated>
      {isMobile && window.location.pathname !== '/dashboard' && <Banner />}
      <Sidebar />
    </Container>
  );
};
